"use client"

import { useId } from "react"
import { useField } from "@payloadcms/ui"
import type { SelectFieldClientComponent } from "payload"

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { FieldShell, resolveLabel } from "./field-shell"

/**
 * Shadcn-themed Payload Select field. Builds a shadcn `<Select>`
 * from the field's `options` array — supports both the
 * `[{ label, value }, ...]` and shorthand `["a", "b"]` formats Payload
 * accepts, and resolves localised labels to the active admin locale.
 *
 * Multi-select is intentionally unsupported here — Payload's
 * hasMany select uses a tag-input UI that we'd need to rebuild from
 * scratch. The shadcn `<Select>` is single-value only. If a field is
 * `hasMany: true` we leave it to Payload's stock component (the
 * applyShadcnFields helper checks this and skips).
 */
export const ShadcnSelectField: SelectFieldClientComponent = (props) => {
  const { path, field } = props
  const fieldName = (field as { name?: string })?.name ?? path
  const { value, setValue, showError, errorMessage } = useField<string>({
    path: path ?? fieldName,
  })
  const id = useId()

  const label = resolveLabel(
    (field as { label?: unknown }).label,
    fieldName,
  )
  const description =
    (field as { admin?: { description?: unknown } })?.admin?.description
  const required = Boolean((field as { required?: boolean }).required)

  type RawOption = string | { label?: unknown; value: string }
  const rawOptions =
    ((field as { options?: RawOption[] }).options as RawOption[] | undefined) ??
    []
  const options = rawOptions.map((opt) =>
    typeof opt === "string"
      ? { label: opt, value: opt }
      : { label: resolveLabel(opt.label, opt.value), value: opt.value },
  )

  return (
    <FieldShell
      label={label}
      required={required}
      description={
        typeof description === "string" || typeof description === "object"
          ? (description as React.ReactNode)
          : undefined
      }
      error={errorMessage}
      showError={showError}
      htmlFor={id}
    >
      <Select
        value={typeof value === "string" ? value : ""}
        onValueChange={(v) => setValue(v)}
      >
        <SelectTrigger
          id={id}
          aria-invalid={showError ? true : undefined}
          className="h-9 rounded-xl"
        >
          <SelectValue placeholder="—" />
        </SelectTrigger>
        <SelectContent>
          {options.map((opt) => (
            <SelectItem key={opt.value} value={opt.value}>
              {opt.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </FieldShell>
  )
}

export default ShadcnSelectField
